<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

/**
 * Переиспользуемая форма «Заявка» + Cloudflare Turnstile.
 * Используется на главной, на странице «О компании» и «Документы и требования».
 * Отправляет данные на POST /api/contact (бэкенд).
 */
const props = withDefaults(
  defineProps<{
    /** Префикс id полей, чтобы избежать конфликтов на странице. */
    idPrefix?: string;
    /** Показывать ли поле «Вы обращаетесь как». */
    showWho?: boolean;
  }>(),
  { idPrefix: 'lead', showWho: false },
);

const nameId = `${props.idPrefix}-name`;
const phoneId = `${props.idPrefix}-phone`;
const whoId = `${props.idPrefix}-who`;
const consentId = `${props.idPrefix}-consent`;

const name = ref('');
const phone = ref('');
const who = ref('Физическое лицо');
const consent = ref(false);

const submitting = ref(false);
const submitted = ref(false);
const error = ref('');

/* ── Cloudflare Turnstile ─────────────────────────────────────── */
// Sitekey виджета берётся из VITE_TURNSTILE_SITE_KEY:
// dev — тестовый ключ (.env.development), сборка — боевой (.env.production).
const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim();
const showTurnstile = computed(() => Boolean(siteKey));

/** Контейнер, в который Turnstile отрисует виджет. */
const turnstileContainer = ref<HTMLElement | null>(null);
const turnstileUnavailable = ref(false);
let turnstileWidgetId: string | undefined;

/** Загружает скрипт Turnstile, если он ещё не подключён в HTML. */
function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src^="https://challenges.cloudflare.com/turnstile"]',
    );
    const script =
      existing ??
      Object.assign(document.createElement('script'), {
        src: 'https://challenges.cloudflare.com/turnstile/v0/api.js',
        async: true,
        defer: true,
      });
    if (!existing) {
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener(
      'error',
      () => reject(new Error('Не удалось загрузить проверку «Я не робот»')),
      { once: true },
    );
  });
}

/** Ждёт, пока API Turnstile станет доступным. */
function waitForTurnstile(): Promise<void> {
  return new Promise((resolve) => {
    const check = (): void => {
      if (window.turnstile) {
        resolve();
        return;
      }
      window.setTimeout(check, 60);
    };
    check();
  });
}

/** Рисует виджет в контейнере и сохраняет его id. */
function renderTurnstile(): void {
  const container = turnstileContainer.value;
  if (!container || !siteKey || !window.turnstile) return;
  turnstileWidgetId = window.turnstile.render(container, {
    sitekey: siteKey,
    theme: 'auto',
    action: 'contact-form',
  });
}

/** Текущий токен виджета ('' — если проверка ещё не пройдена). */
function getTurnstileToken(): string {
  if (!window.turnstile || !turnstileWidgetId) return '';
  return window.turnstile.getResponse(turnstileWidgetId) ?? '';
}

/**
 * Ждёт появления токена (managed-виджет решается автоматически за ~1 c,
 * у интерактивного — после клика по чекбоксу). Возвращает токен или ''.
 */
function waitForTurnstileToken(timeoutMs = 3000): Promise<string> {
  return new Promise((resolve) => {
    const started = Date.now();
    const poll = (): void => {
      const token = getTurnstileToken();
      if (token) {
        resolve(token);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve('');
        return;
      }
      window.setTimeout(poll, 120);
    };
    poll();
  });
}

/**
 * Обновляет виджет. Вызывается после неудачной серверной проверки токена
 * (403/503), чтобы посетитель мог пройти проверку заново.
 */
function resetTurnstile(): void {
  if (window.turnstile && turnstileWidgetId) {
    window.turnstile.reset(turnstileWidgetId);
  }
}

function removeTurnstile(): void {
  if (window.turnstile && turnstileWidgetId) {
    window.turnstile.remove(turnstileWidgetId);
    turnstileWidgetId = undefined;
  }
}

/**
 * Загружает скрипт Turnstile (если ещё не загружен) и рисует виджет.
 * Виджет и обращение к Cloudflare появляются ТОЛЬКО после того, как посетитель
 * отметил согласие на обработку персональных данных. До этого момента на
 * странице ничего не загружается и не проверяется.
 */
async function ensureTurnstileRendered(): Promise<void> {
  if (!showTurnstile.value || turnstileUnavailable.value || turnstileWidgetId) return;
  try {
    await loadTurnstileScript();
    await waitForTurnstile();
    await nextTick();
    renderTurnstile();
  } catch {
    turnstileUnavailable.value = true;
  }
}

// Отметили согласие — включаем проверку «Я не робот».
// Сняли галочку — убираем виджет, чтобы при повторной отметке проверка шла заново.
watch(consent, async (checked) => {
  if (!checked) {
    removeTurnstile();
    return;
  }
  await ensureTurnstileRendered();
});

onBeforeUnmount(() => {
  removeTurnstile();
});

// После успешной отправки виджет больше не нужен.
watch(submitted, (isSubmitted) => {
  if (isSubmitted) removeTurnstile();
});

/** Отправка заявки на сервер: POST /api/contact */
async function submitForm(): Promise<void> {
  error.value = '';

  let token = '';
  if (showTurnstile.value) {
    // Виджет рисуется только после отметки согласия. Если посетитель отправил
    // форму сразу после клика по чекбоксу — дожидаемся появления виджета.
    await ensureTurnstileRendered();
    // Даём виджету время авто-решиться, и только потом ругаемся.
    token = turnstileUnavailable.value ? '' : await waitForTurnstileToken();
    if (!token) {
      error.value = turnstileUnavailable.value
        ? 'Не удалось загрузить проверку «Я не робот». Обновите страницу.'
        : 'Пожалуйста, подтвердите, что вы не робот.';
      return;
    }
  }

  submitting.value = true;

  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name.value.trim(),
        phone: phone.value.trim(),
        who: props.showWho ? who.value : undefined,
        consent: consent.value,
        turnstileToken: token,
      }),
    });

    if (!response.ok) {
      let message = 'Не удалось отправить заявку. Попробуйте ещё раз.';
      if (response.status === 403) {
        // Turnstile не прошёл серверную проверку — обновляем виджет.
        resetTurnstile();
        message = 'Проверка «Я не робот» не пройдена. Попробуйте ещё раз.';
      } else if (response.status === 429) {
        message = 'Превышен лимит заявок с одного адреса. Попробуйте позже.';
      } else if (response.status === 503) {
        // Сервер не смог подтвердить проверку — даём пройти ещё раз.
        resetTurnstile();
        message = 'Не удалось отправить заявку. Попробуйте ещё раз чуть позже.';
      } else {
        try {
          const data = (await response.json()) as { message?: string | string[] };
          if (data?.message) {
            message = Array.isArray(data.message)
              ? data.message.join(', ')
              : data.message;
          }
        } catch {
          // тело ответа не JSON — оставляем сообщение по умолчанию
        }
      }
      throw new Error(message);
    }

    submitted.value = true;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Не удалось отправить заявку.';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <form class="card form-card" @submit.prevent="submitForm">
    <div v-if="!submitted">
      <div class="grid grid-cols-2 grid-gap-sm">
        <div class="field">
          <label :for="nameId">Имя</label>
          <input :id="nameId" v-model="name" type="text" required placeholder="Ваше имя" />
        </div>
        <div class="field">
          <label :for="phoneId">Телефон</label>
          <input :id="phoneId" v-model="phone" type="tel" required placeholder="+375 (__) ___-__-__" />
        </div>
      </div>

      <div v-if="showWho" class="field">
        <label :for="whoId">Вы обращаетесь как</label>
        <select :id="whoId" v-model="who">
          <option>Физическое лицо</option>
          <option>Индивидуальный предприниматель</option>
          <option>Юридическое лицо</option>
        </select>
      </div>

      <div class="checkbox-row form-consent">
        <input :id="consentId" v-model="consent" type="checkbox" required />
        <label :for="consentId">
          Согласен на <a href="/docs/tc_personal-data-processing-policy.pdf" style="color: var(--color-primary); text-decoration:underline;">обработку персональных данных</a> для рассмотрения заявки
        </label>
      </div>

      <!-- Виджет Cloudflare Turnstile («Я не робот») появляется после отметки согласия на обработку данных -->
      <div v-if="showTurnstile" ref="turnstileContainer" class="turnstile-wrap"></div>
      <p v-if="turnstileUnavailable" class="form-error" role="alert">
        Не удалось загрузить проверку «Я не робот». Обновите страницу.
      </p>

      <button type="submit" class="btn btn-primary btn-block" :disabled="submitting">
        {{ submitting ? 'Отправляем…' : 'Узнать условия' }}
      </button>

      <p v-if="error" class="form-error" role="alert">{{ error }}</p>
    </div>

    <div v-else class="form-success">
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--color-success)"
        stroke-width="2"
        style="margin-inline: auto; margin-bottom: var(--space-4)"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12l3 3 5-6" />
      </svg>
      <h3 class="form-success__title">Заявка отправлена</h3>
      <p class="form-success__text">
        Специалист свяжется с вами в ближайшее рабочее время.
      </p>
    </div>
  </form>
</template>

<style scoped>
/* Область виджета Cloudflare Turnstile */
.turnstile-wrap {
  min-height: 65px;
  margin: var(--space-4) 0;
}

.turnstile-wrap:empty {
  display: none;
}
</style>
